import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useRegisterInput from '../Form/useRegisterInput';
import Form from '../Form';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import useForm from '@afosto/utils-react/validation/useForm';
import PaymentMethodSearch from '../PaymentMethodSearch';
import Autocomplete from '../Autocomplete';
import useIntl from '@afosto/utils-react/intl/useIntl';
import translations from './translations';

const PaymentMethodForm = ({ clientId, paymentMethod, onCancel, onSubmit }) => {
  const intl = useIntl();
  const [methodIssuers, setMethodIssuers] = useState([]);

  const { method, provider } = paymentMethod ?? {};
  const { issuers } = method ?? {};
  const formattedMethod = method
    ? {
        ...method,
        label: intl.formatMessage({
          id: `paymentMethods.${provider?.toLowerCase()}.${method?.code}`,
          defaultMessage: method?.name,
          description: `label of the payment method for code ${method?.code}`,
        }),
        value: method?.code,
      }
    : null;

  const form = useForm({
    defaultValues: {
      method: formattedMethod,
      provider: provider ?? null,
      issuer: paymentMethod?.issuer ?? null,
    },
    onCancel,
    onSubmit,
  });

  const { setValue, watch } = form;
  useRegisterInput('provider', true, form);
  useRegisterInput('issuer', true, form);

  const issuerValue = watch('issuer');

  const handleMethodChange = (event, newValue) => {
    setValue('method', newValue);
    setValue('provider', newValue?.provider_code);
    setValue('issuer', null);

    setMethodIssuers(
      (newValue?.issuers ?? []).reduce(
        (acc, issuer) => [...acc, { ...issuer, label: issuer.name, value: issuer.code }],
        [],
      ),
    );
  };

  const showIssuerField = methodIssuers?.length > 0 || issuerValue?.code;

  useEffect(() => {
    if (issuers && issuers.length > 0) {
      setMethodIssuers(
        issuers.reduce(
          (acc, issuer) => [...acc, { ...issuer, label: issuer.name, value: issuer.code }],
          [],
        ),
      );
    } else {
      setMethodIssuers([]);
    }
  }, [issuers]);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <PaymentMethodSearch
          name="method"
          label={intl.formatMessage(translations.fields.paymentMethod.label)}
          onChange={handleMethodChange}
          clientId={clientId}
        />
        {showIssuerField && (
          <Autocomplete
            name="issuer-field"
            options={methodIssuers}
            label={intl.formatMessage(translations.fields.paymentMethodIssuer.label)}
            register={false}
            value={issuerValue ?? []}
            onChange={(event, newValue) => setValue('issuer', newValue)}
            autocomplete="off"
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

PaymentMethodForm.propTypes = {
  clientId: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  paymentMethod: PropTypes.object,
};

PaymentMethodForm.defaultProps = {
  clientId: undefined,
  onCancel: undefined,
  onSubmit: undefined,
  paymentMethod: null,
};

export default PaymentMethodForm;
